import React, { useState, lazy } from 'react';
import { AnimatedSection } from '../shared/AnimatedSection';
import { ArtifactCard } from '../shared/ArtifactCard';
import { Sign } from '../../data/signs';
import { motion } from 'framer-motion';
import { startCheckout } from '../../lib/metaPixel';
interface SignProductosProps {
  sign: Sign;
}
export function SignProductos({
  sign
}: SignProductosProps) {
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [isBundle, setIsBundle] = useState(false);
  const coreProduct = {
    title: `Núcleo del Arquetipo ${sign.name}`,
    price: 9.99
  };
  const addOns = [{
    id: 'sombrio',
    title: 'Archivo Sombrío',
    description: `Explora los patrones ocultos, heridas simbólicas y reacciones internas que ${sign.name} suele esconder.`,
    price: 9
  }, {
    id: 'compatibilidad',
    title: 'Códice de Compatibilidad',
    description: `Descubre cómo se relaciona ${sign.name} con otras energías: atracción, tensión, choque y magnetismo.`,
    price: 10
  }, {
    id: 'ritual',
    title: 'Ritual del Eclipse',
    description: `Un ritual simbólico de conexión con la energía de ${sign.name} para integrar su fuerza de forma consciente.`,
    price: 8
  }, {
    id: 'camara',
    title: 'Cámara Astral',
    description: `Una experiencia sonora/visual complementaria para sumergirte en la atmósfera del reino de ${sign.name}.`,
    price: 12
  }];
  const bundlePrice = 34.99;
  const toggleAddOn = (id: string) => {
    if (isBundle) return;
    setSelectedAddOns(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleBundle = () => {
    setIsBundle(!isBundle);
    if (!isBundle) {
      setSelectedAddOns([]);
    }
  };
  const calculateTotal = () => {
    if (isBundle) return bundlePrice;
    const addOnTotal = selectedAddOns.reduce((sum, id) => {
      const addOn = addOns.find(a => a.id === id);
      return sum + (addOn?.price || 0);
    }, 0);
    return coreProduct.price + addOnTotal;
  };
  const PRODUCTOS_BG = 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/mm8DHXzU7JhvCgYay4N1kmyc5Q2WN4pg9GNtp0heMg7BSrM3QpCC0xGOYBCXzy5-GOp2Z8fowqxSWnS4ldgA120xXC67zpfx2qL_wjuAepxGwDo6ErItRpus49bMEA7U.jpg?v=1778629864';
  return <section className="relative py-20 px-6 overflow-hidden" data-id="element-271">
      {/* Background image tinted with sign color */}
      <div className="absolute inset-0 z-0" data-id="element-272">
        <img src={PRODUCTOS_BG} alt="" aria-hidden="true" className="w-full h-full object-cover" loading="lazy" data-id="element-273" />
        <div className="absolute inset-0 bg-cosmic-black/80" data-id="element-274" />
        <div className="absolute inset-0" style={{
        backgroundColor: sign.colors.primary,
        opacity: 0.28
      }} data-id="element-275" />
        <div className="absolute inset-0" style={{
        background: `linear-gradient(180deg, #050508 0%, ${sign.colors.primary}15 50%, #050508 100%)`
      }} data-id="element-276" />
      </div>
      <div className="relative z-10 container mx-auto max-w-6xl" data-id="element-277">
        <AnimatedSection data-id="element-278">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-center text-white mb-4" data-id="element-279">
            Desbloquea Tu Reino
          </h2>
          <p className="text-center mb-16 text-lg italic" style={{
          color: sign.colors.accent
        }} data-id="element-280">
            Elige tu experiencia {sign.name}
          </p>
        </AnimatedSection>

        {/* Core Product */}
        <AnimatedSection delay={0.2} data-id="element-281">
          <div className="mb-8 max-w-2xl mx-auto" data-id="element-282">
            <div className="border-2 rounded-xl p-8 text-center" style={{
            borderColor: sign.colors.primary,
            backgroundColor: `${sign.colors.primary}10`,
            boxShadow: `0 0 40px ${sign.colors.primary}30`
          }} data-id="element-283">
              <h3 className="text-3xl font-serif font-bold mb-4" style={{
              color: sign.colors.accent
            }} data-id="element-284">
                {coreProduct.title}
              </h3>
              <p className="text-gray-300 mb-6" data-id="element-285">
                La experiencia fundamental de tu arquetipo
              </p>
              <p className="text-4xl font-bold mb-6" style={{
              color: sign.colors.primary
            }} data-id="element-286">
                ${coreProduct.price}
              </p>
              <p className="text-sm text-gray-400" data-id="element-287">
                Incluido en todas las opciones
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* Add-ons */}
        <AnimatedSection delay={0.4} data-id="element-288">
          <h3 className="text-2xl font-serif font-semibold text-white text-center mb-8" data-id="element-289">
            Archivos Adicionales
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-290">
            {addOns.map((addOn, index) => <motion.div key={addOn.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.5 + index * 0.1
          }} data-id="element-291">
                <ArtifactCard title={addOn.title} description={addOn.description} price={`+$${addOn.price}`} selected={isBundle || selectedAddOns.includes(addOn.id)} onToggle={() => toggleAddOn(addOn.id)} signColor={sign.colors.primary} data-id="element-292" />
              </motion.div>)}
          </div>
        </AnimatedSection>

        {/* Bundle */}
        <AnimatedSection delay={0.8} data-id="element-293">
          <div className="max-w-2xl mx-auto mb-12" data-id="element-294">
            <ArtifactCard title={`Acceso Completo al Reino ${sign.name}`} description={`Desbloquea el Núcleo de ${sign.name} y todos los archivos adicionales en una sola experiencia.`} price={`$${bundlePrice}`} selected={isBundle} onToggle={toggleBundle} signColor={sign.colors.accent} isBundle={true} data-id="element-295" />
          </div>
        </AnimatedSection>

        {/* Dynamic Total */}
        <motion.div className="sticky bottom-6 max-w-2xl mx-auto bg-cosmic-black border-2 rounded-xl p-6" style={{
        borderColor: sign.colors.primary,
        boxShadow: `0 0 40px ${sign.colors.primary}40`
      }} initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-296">
          <div className="flex justify-between items-center mb-4" data-id="element-297">
            <div data-id="element-298">
              <p className="text-gray-400 text-sm mb-1" data-id="element-299">Tu selección:</p>
              <p className="text-white font-medium" data-id="element-300">
                {isBundle ? `Reino Completo ${sign.name}` : `Núcleo ${sign.name}${selectedAddOns.length > 0 ? ` + ${selectedAddOns.length} archivo${selectedAddOns.length > 1 ? 's' : ''}` : ''}`}
              </p>
            </div>
            <div className="text-right" data-id="element-301">
              <p className="text-gray-400 text-sm mb-1" data-id="element-302">Total:</p>
              <p className="text-3xl font-bold" style={{
              color: sign.colors.accent
            }} data-id="element-303">
                ${calculateTotal().toFixed(2)}
              </p>
            </div>
          </div>
          <motion.button onClick={() => startCheckout(sign.name, sign.checkoutUrl, calculateTotal())} className="block w-full text-center py-4 font-bold rounded-lg transition-colors" style={{
          backgroundColor: sign.colors.primary,
          color: '#050508'
        }} whileHover={{
          scale: 1.02,
          boxShadow: `0 0 30px ${sign.colors.primary}80`
        }} whileTap={{
          scale: 0.98
        }} data-id="element-304">
            {isBundle ? `Desbloquear Reino Completo ${sign.name}` : `Entrar a mi Reino ${sign.name}`}
          </motion.button>
        </motion.div>
      </div>
    </section>;
}